import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useAuth } from "../../../context/AuthContext";
import { registrarAccesorio, getTiposAccesorioCatalogo, getModelosPorTipoCatalogo } from "../services/registroEquiposService";
import { CIUDADES, normalizeCiudad } from "./EquipoSerializadoForm";

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 12.5 },
  "& .MuiOutlinedInput-root": { fontSize: 13 },
};

const DESTINOS = [
  { value: "almacen", label: "Almacén" },
  { value: "buen_estado", label: "Laboratorio" },
];

const initialValues = {
  id_servicio_detalle: "",
  id_equipo: "",
  cantidad: "",
  destino: "almacen",
  observacion: "",
};

// Igual que accesorio.php/Guarda_accesorio.php del sistema viejo: el modelo final no es
// texto libre, se arma en el backend concatenando el "Tipo" (detalle_servicio) con el
// "Modelo especifico" (tipo_equipo, dependiente del tipo elegido) para no fragmentar el
// catálogo. "Destino" decide si la cantidad suma a almacen (sin revisar) o a buen_estado
// (ya apto para entrega), igual que el select "Ingresarán a: Almacén/Laboratorio" del PHP.
export default function RegistrarAccesorioForm() {
  const { user } = useAuth();
  const [tipos, setTipos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [cargandoModelos, setCargandoModelos] = useState(false);
  const [values, setValues] = useState({ ...initialValues, ciudad: normalizeCiudad(user?.sucursal) });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [mensajeExito, setMensajeExito] = useState("");

  useEffect(() => {
    if (!user?.sucursal) return;
    setValues((prev) => (prev.ciudad ? prev : { ...prev, ciudad: normalizeCiudad(user.sucursal) }));
  }, [user?.sucursal]);

  useEffect(() => {
    getTiposAccesorioCatalogo()
      .then(({ data }) => setTipos(Array.isArray(data) ? data : []))
      .catch(() => setTipos([]));
  }, []);

  useEffect(() => {
    if (!values.id_servicio_detalle) {
      setModelos([]);
      return;
    }
    let active = true;
    setCargandoModelos(true);
    getModelosPorTipoCatalogo(values.id_servicio_detalle)
      .then(({ data }) => {
        if (active) setModelos(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setModelos([]);
      })
      .finally(() => {
        if (active) setCargandoModelos(false);
      });
    return () => {
      active = false;
    };
  }, [values.id_servicio_detalle]);

  const setValue = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const onTipoChange = (value) => {
    setValues((prev) => ({ ...prev, id_servicio_detalle: value, id_equipo: "" }));
  };

  const requiredMissing =
    !values.id_servicio_detalle ||
    !values.id_equipo ||
    !values.ciudad.trim() ||
    !values.destino ||
    !values.cantidad ||
    Number(values.cantidad) <= 0;

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const { data } = await registrarAccesorio({
        id_servicio_detalle: Number(values.id_servicio_detalle),
        id_equipo: values.id_equipo ? Number(values.id_equipo) : undefined,
        ciudad: values.ciudad,
        cantidad: Number(values.cantidad),
        destino: values.destino,
        observacion: values.observacion || undefined,
        usu: user?.username,
      });
      setValues({ ...initialValues, ciudad: values.ciudad });
      setModelos([]);
      const destinoLabel = DESTINOS.find((d) => d.value === data.destino)?.label || data.destino;
      setMensajeExito(`Stock actualizado: "${data.modelo}" ahora tiene ${data[data.destino]} unidades en ${destinoLabel} (${data.ciudad}).`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "No se pudo registrar el accesorio.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.25 }}>
        Registrar Accesorio
      </Typography>

      {!!error && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1.25, fontSize: 12.5 }}>
          {error}
        </MuiAlert>
      )}

      <Grid container spacing={1.25}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx}>
            <InputLabel>Tipo de accesorio</InputLabel>
            <Select label="Tipo de accesorio" value={values.id_servicio_detalle} onChange={(e) => onTipoChange(e.target.value)}>
              {tipos.map((t) => (
                <MenuItem key={t.id_servicio_detalle} value={t.id_servicio_detalle}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx} disabled={!values.id_servicio_detalle || cargandoModelos}>
            <InputLabel>Modelo específico</InputLabel>
            <Select label="Modelo específico" value={values.id_equipo} onChange={(e) => setValue("id_equipo", e.target.value)}>
              {modelos.map((m) => (
                <MenuItem key={m.id_equipo} value={m.id_equipo}>
                  {m.nombre || m.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx}>
            <InputLabel>Ciudad</InputLabel>
            <Select label="Ciudad" value={values.ciudad} onChange={(e) => setValue("ciudad", e.target.value)}>
              {CIUDADES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx}>
            <InputLabel>Ingresarán a</InputLabel>
            <Select label="Ingresarán a" value={values.destino} onChange={(e) => setValue("destino", e.target.value)}>
              {DESTINOS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Cantidad a ingresar"
            value={values.cantidad}
            onChange={(e) => setValue("cantidad", e.target.value)}
            required
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            size="small"
            sx={controlSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Observación"
            value={values.observacion}
            onChange={(e) => setValue("observacion", e.target.value)}
            multiline
            minRows={3}
            fullWidth
            size="small"
            sx={controlSx}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          disabled={requiredMissing || guardando}
          onClick={guardar}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Registrar
        </Button>
      </Box>

      <Snackbar
        open={!!mensajeExito}
        autoHideDuration={4000}
        onClose={() => setMensajeExito("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setMensajeExito("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {mensajeExito}
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}
